import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { MAIN_URL } from "../../../../Configs/Urls";
import { getLocalStorageJSON } from "../../../../Utils/localStorageFunc";
import CancellationForm from "./CancellationForm";
import ReschedulingForm from "./ReschedulingForm";
import Popup1 from "../../../Components/Popups/ModalPopup/Popup1";
import "./formStyle.css";

const statusColor = (status) => {
  if (!status) return "#888";
  const s = status.toLowerCase();
  if (s === "pending") return "#f59e0b";
  if (s === "completed" || s === "resolved") return "#16a34a";
  if (s === "cancelled" || s === "rejected") return "#dc2626";
  return "#3b82f6";
};

const PER_PAGE_OPTIONS = [5, 10, 20];

function RaisedRequests({ booking_id, onClose }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [formAutoFill, setFormAutoFill] = useState(null);
  const [formDocumentData, setFormDocumentData] = useState(null);

  const { isUserLogin } = useSelector((state) => state.user);

  useEffect(() => {
    if (!booking_id) return;
    setLoading(true);
    const token = getLocalStorageJSON(isUserLogin ? "UL" : "AL");
    axios
      .get(`${MAIN_URL}bookings/flights/user-booking-requests/${booking_id}`, {
        params: { page, per_page: perPage },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setRequests(res.data?.requests || []);
        setTotal(res.data?.total ?? 0);
        setLastPage(res.data?.last_page ?? 1);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [booking_id, isUserLogin, page, perPage]);

  const handleRowClick = async (req) => {
    if (!req.enquiry_id) return;
    try {
      const token = getLocalStorageJSON(isUserLogin ? "UL" : "AL");
      const res = await axios.get(`${MAIN_URL}bookings/flights/post-booking-form-by-id/${req.form_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const fd = res.data;

      // Build docs in the shape the form expects
      const mappedDocs = { trip_1: [], trip_2: [], trip_3: [], trip_4: [] };
      (fd.documents || []).forEach((doc) => {
        const key = doc.document_for || "trip_1";
        if (mappedDocs[key]) {
          mappedDocs[key].push({ name: doc.document_link.split("/").pop(), url: doc.document_link, isExisting: true });
        }
      });

      // autoFill.data must mirror the full API response shape the form expects:
      // autoFill.data.data = inner data object with form_data, user_remarks, proceed_mode
      // autoFill.data.submittion_type = for disable-check
      // autoFill.data.selectedTravellers = pre-selected traveller IDs
      const autoFillData = {
        data: {
          ...(fd.data || {}),
          user_remarks_1: fd.user_remarks_1 ?? "",
          user_remarks_2: fd.user_remarks_2 ?? "",
          user_remarks_3: fd.user_remarks_3 ?? "",
          user_remarks_4: fd.user_remarks_4 ?? "",
          proceed_mode_1: fd.proceed_mode_1 ?? 0,
          proceed_mode_2: fd.proceed_mode_2 ?? 0,
          proceed_mode_3: fd.proceed_mode_3 ?? 0,
          proceed_mode_4: fd.proceed_mode_4 ?? 0,
        },
        submittion_type: fd.submittion_type ?? "user",
        selectedTravellers: fd.selected_traveller_ids || [],
      };

      setFormAutoFill({ autoFilled: true, data: autoFillData });
      setFormDocumentData(mappedDocs);
      setSelectedRequest(req);
    } catch (err) {
      console.error("Failed to load form data", err);
    }
  };

  return (
    <div style={{ padding: "24px", minWidth: "700px", maxWidth: "900px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "#1e293b" }}>
          Raised Requests
        </h2>
        {onClose && (
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "20px", color: "#64748b" }}>
            ✕
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>Loading...</div>
      ) : requests.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
          No requests raised yet.
        </div>
      ) : (
        <>
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {requests.map((req) => (
            <div
              key={req.id}
              onClick={() => handleRowClick(req)}
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                padding: "14px 18px",
                cursor: req.enquiry_id ? "pointer" : "default",
                background: "#fff",
                transition: "box-shadow 0.15s",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              }}
              onMouseEnter={(e) => { if (req.enquiry_id) e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)"; }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
                  <div>
                    <span style={{ fontSize: "12px", color: "#94a3b8" }}>Request ID</span>
                    <div style={{ fontWeight: 700, color: "#1e293b", fontSize: "15px" }}>#{req.id}</div>
                  </div>
                  <div
                    style={{
                      padding: "3px 12px",
                      borderRadius: "20px",
                      fontSize: "12px",
                      fontWeight: 600,
                      background: req.type === "cancellation" ? "#fee2e2" : "#dbeafe",
                      color: req.type === "cancellation" ? "#b91c1c" : "#1d4ed8",
                      textTransform: "capitalize",
                    }}
                  >
                    {req.type === "cancellation" ? "Cancellation" : "Reschedule"}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <span style={{ fontSize: "12px", color: "#94a3b8" }}>Enquiry Status</span>
                  <div>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "2px 10px",
                        borderRadius: "20px",
                        fontSize: "12px",
                        fontWeight: 600,
                        background: statusColor(req.enquiry_status) + "22",
                        color: statusColor(req.enquiry_status),
                        border: `1px solid ${statusColor(req.enquiry_status)}44`,
                        textTransform: "capitalize",
                      }}
                    >
                      {req.enquiry_status || "—"}
                    </span>
                  </div>
                </div>
              </div>

              {req.type === "cancellation" && (req.cancellation_message_1 || req.cancellation_message_2) && (
                <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #f1f5f9" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 0 }}>

                    {/* Step 1 — Cancellation */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                      <div style={{
                        width: "28px", height: "28px", borderRadius: "50%",
                        background: req.cancellation_message_1 ? "#dc2626" : "#e2e8f0",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "12px", fontWeight: 700, color: req.cancellation_message_1 ? "#fff" : "#94a3b8",
                        flexShrink: 0,
                      }}>1</div>
                    </div>

                    {/* Connector */}
                    <div style={{ flex: 1, height: "2px", background: req.cancellation_message_1 && req.cancellation_message_2 ? "#dc2626" : "#e2e8f0", marginTop: "13px", minWidth: "16px" }} />

                    {/* Step 2 — Refund */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                      <div style={{
                        width: "28px", height: "28px", borderRadius: "50%",
                        background: req.cancellation_message_2 ? "#16a34a" : "#e2e8f0",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "12px", fontWeight: 700, color: req.cancellation_message_2 ? "#fff" : "#94a3b8",
                        flexShrink: 0,
                      }}>2</div>
                    </div>
                  </div>

                  {/* Labels row */}
                  <div style={{ display: "flex", alignItems: "flex-start", marginTop: "6px" }}>
                    <div style={{ flex: 1, paddingRight: "8px" }}>
                      <div style={{ fontSize: "10px", fontWeight: 600, color: req.cancellation_message_1 ? "#dc2626" : "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        Cancellation
                      </div>
                      <div style={{ fontSize: "11px", color: req.cancellation_message_1 ? "#1e293b" : "#94a3b8", marginTop: "2px", fontWeight: req.cancellation_message_1 ? 500 : 400 }}>
                        {req.cancellation_message_1 || "Pending"}
                      </div>
                    </div>
                    <div style={{ flex: 1, paddingLeft: "8px", textAlign: "right" }}>
                      <div style={{ fontSize: "10px", fontWeight: 600, color: req.cancellation_message_2 ? "#16a34a" : "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        Refund
                      </div>
                      <div style={{ fontSize: "11px", color: req.cancellation_message_2 ? "#1e293b" : "#94a3b8", marginTop: "2px", fontWeight: req.cancellation_message_2 ? 500 : 400 }}>
                        {req.cancellation_message_2 || "Pending"}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* {[req.user_remarks_1, req.user_remarks_2, req.user_remarks_3, req.user_remarks_4].some(Boolean) && (
                <div style={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px solid #f1f5f9", display: "flex", flexDirection: "column", gap: "4px" }}>
                  {[req.user_remarks_1, req.user_remarks_2, req.user_remarks_3, req.user_remarks_4].map((remark, i) =>
                    remark ? (
                      <div key={i} style={{ fontSize: "12px" }}>
                        <span style={{ color: "#94a3b8" }}>Remarks {i + 1}: </span>
                        <span style={{ color: "#334155" }}>{remark}</span>
                      </div>
                    ) : null
                  )}
                </div>
              )} */}

              <div style={{ marginTop: "6px", fontSize: "11px", color: "#94a3b8" }}>
                {req.created_at ? new Date(req.created_at).toLocaleString() : ""}
                {req.enquiry_id && <span style={{ marginLeft: "8px", color: "#3b82f6" }}>Click to view →</span>}
              </div>
            </div>
          ))}
        </div>

        {/* Pagination controls */}
        {lastPage > 1 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "16px", gap: "12px", flexWrap: "wrap" }}>

            {/* Per-page selector */}
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ fontSize: "12px", color: "#64748b" }}>Per page:</span>
              {PER_PAGE_OPTIONS.map((opt) => (
                <button
                  key={opt}
                  onClick={() => { setPerPage(opt); setPage(1); }}
                  style={{
                    padding: "3px 10px", borderRadius: "6px", fontSize: "12px", fontWeight: 600,
                    border: perPage === opt ? "none" : "1px solid #e2e8f0",
                    background: perPage === opt ? "#3b82f6" : "#fff",
                    color: perPage === opt ? "#fff" : "#334155",
                    cursor: "pointer",
                  }}
                >
                  {opt}
                </button>
              ))}
            </div>

            {/* Page nav */}
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{
                  padding: "5px 14px", borderRadius: "6px", border: "1px solid #e2e8f0",
                  background: page === 1 ? "#f8fafc" : "#fff", color: page === 1 ? "#cbd5e1" : "#334155",
                  cursor: page === 1 ? "not-allowed" : "pointer", fontSize: "13px", fontWeight: 500,
                }}
              >
                ← Prev
              </button>

              {Array.from({ length: lastPage }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === lastPage || Math.abs(p - page) <= 1)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, idx) =>
                  p === "..." ? (
                    <span key={`ellipsis-${idx}`} style={{ fontSize: "13px", color: "#94a3b8", padding: "0 2px" }}>…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      style={{
                        padding: "5px 11px", borderRadius: "6px", fontSize: "13px", fontWeight: 600,
                        border: p === page ? "none" : "1px solid #e2e8f0",
                        background: p === page ? "#3b82f6" : "#fff",
                        color: p === page ? "#fff" : "#334155",
                        cursor: "pointer",
                      }}
                    >
                      {p}
                    </button>
                  )
                )}

              <button
                onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                disabled={page === lastPage}
                style={{
                  padding: "5px 14px", borderRadius: "6px", border: "1px solid #e2e8f0",
                  background: page === lastPage ? "#f8fafc" : "#fff",
                  color: page === lastPage ? "#cbd5e1" : "#334155",
                  cursor: page === lastPage ? "not-allowed" : "pointer",
                  fontSize: "13px", fontWeight: 500,
                }}
              >
                Next →
              </button>
            </div>

            {/* Record count */}
            <span style={{ fontSize: "12px", color: "#94a3b8" }}>
              {(page - 1) * perPage + 1}–{Math.min(page * perPage, total)} of {total}
            </span>
          </div>
        )}
        </>
      )}

      {selectedRequest && selectedRequest.type === "cancellation" && (
        <Popup1
          Open={true}
          onClose={() => { setSelectedRequest(null); setFormAutoFill(null); }}
          Content={
            <CancellationForm
              showbottombar={false}
              readOnly={true}
              getApi={`${MAIN_URL}bookings/flights/get-cancellation-form`}
              postApi={`${MAIN_URL}bookings/flights/move-to-booking/cancellation-v1`}
              booking_id={booking_id}
              formId={selectedRequest.form_id}
              requestId={selectedRequest.id}
              autoFill={formAutoFill || { autoFilled: false }}
              documentData={formDocumentData || { trip_1: [], trip_2: [], trip_3: [], trip_4: [] }}
              onClose={() => { setSelectedRequest(null); setFormAutoFill(null); }}
            />
          }
        />
      )}

      {selectedRequest && selectedRequest.type === "reschedule" && (
        <Popup1
          Open={true}
          onClose={() => { setSelectedRequest(null); setFormAutoFill(null); }}
          Content={
            <ReschedulingForm
              showbottombar={false}
              readOnly={true}
              getApi={`${MAIN_URL}bookings/flights/get-rescheduling-form`}
              postApi={`${MAIN_URL}bookings/flights/move-to-booking/rescheduling-v1`}
              booking_id={booking_id}
              formId={selectedRequest.form_id}
              requestId={selectedRequest.id}
              autoFill={formAutoFill || { autoFilled: false }}
              documentData={formDocumentData || { trip_1: [], trip_2: [], trip_3: [], trip_4: [] }}
              onClose={() => { setSelectedRequest(null); setFormAutoFill(null); }}
            />
          }
        />
      )}
    </div>
  );
}

export default RaisedRequests;
